import { useState, FormEvent, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Mail, Phone, MapPin, CheckCircle, AlertCircle } from 'lucide-react';

type FieldName = 'name' | 'email' | 'company' | 'message';

type FormState = Record<FieldName, string>;
type TouchedState = Record<FieldName, boolean>;

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = (data: FormState) => {
  const errors: Partial<Record<FieldName, string>> = {};
  if (!data.name.trim()) errors.name = 'Name is required';
  else if (data.name.trim().length < 2) errors.name = 'Name must be at least 2 characters';

  if (!data.email.trim()) errors.email = 'Email is required';
  else if (!emailRegex.test(data.email.trim())) errors.email = 'Please enter a valid email';

  if (!data.message.trim()) errors.message = 'Message is required';
  else if (data.message.trim().length < 10) errors.message = 'Message should be at least 10 characters';

  return errors;
};

const fieldBase =
  'w-full px-4 py-3 border rounded-xl bg-white dark:bg-secondary/40 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:border-transparent outline-none transition-all';

const Contact = () => {
  const [formData, setFormData] = useState<FormState>({ name: '', email: '', company: '', message: '' });
  const [touched, setTouched] = useState<TouchedState>({ name: false, email: false, company: false, message: false });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const errors = useMemo(() => validate(formData), [formData]);
  const isValid = Object.keys(errors).length === 0;

  const handleChange = (field: FieldName, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleBlur = (field: FieldName) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const fieldClass = (field: FieldName) => {
    const showError = touched[field] && errors[field];
    if (showError) return `${fieldBase} border-red-400 focus:ring-red-400`;
    return `${fieldBase} border-gray-200 dark:border-white/10 focus:ring-primary dark:focus:ring-highlight`;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched({ name: true, email: true, company: true, message: true });
    if (!isValid) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', company: '', message: '' });
        setTouched({ name: false, email: false, company: false, message: false });
      } else {
        setSubmitError('Something went wrong. Please try again or email us directly.');
      }
    } catch {
      setSubmitError("Couldn't reach the server. Please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gray-50 dark:bg-secondary/20 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-highlight font-medium text-sm uppercase tracking-wider">Get In Touch</span>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-primary dark:text-white mt-3 mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-lg">
            Let's discuss how we can help elevate your online presence and grow your business.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-2xl font-bold text-primary dark:text-white mb-6">
              Let's Build Something Great Together
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Whether you're starting from scratch or looking to redesign your existing website, we're here to help you create a digital presence that truly represents your business.
            </p>

            <div className="space-y-6">
              {[
                { icon: Mail, label: 'Email', value: 'hello@xavieragency.co.uk' },
                { icon: Phone, label: 'Phone', value: '+44 7713018538' },
                { icon: MapPin, label: 'Location', value: 'London, United Kingdom' },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-primary/10 dark:bg-white/5 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Icon className="text-primary dark:text-highlight" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-primary dark:text-white">{label}</p>
                    <p className="text-gray-600 dark:text-gray-400">{value}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6 }}
            className="bg-white dark:bg-secondary/40 dark:border dark:border-white/5 rounded-2xl p-8 shadow-sm"
          >
            <AnimatePresence mode="wait">
              {isSubmitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="text-center py-12"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 18, delay: 0.1 }}
                  >
                    <CheckCircle className="mx-auto text-green-500 mb-4" size={64} />
                  </motion.div>
                  <h3 className="text-2xl font-bold text-primary dark:text-white mb-2">Thank You!</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    We've received your message and will get back to you within 24 hours.
                  </p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSubmit}
                  noValidate
                  className="space-y-5"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      onBlur={() => handleBlur('name')}
                      className={fieldClass('name')}
                      placeholder="Your name"
                      aria-invalid={!!(touched.name && errors.name)}
                    />
                    <FieldError show={touched.name && !!errors.name} message={errors.name} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      onBlur={() => handleBlur('email')}
                      className={fieldClass('email')}
                      placeholder="your@email.com"
                      aria-invalid={!!(touched.email && errors.email)}
                    />
                    <FieldError show={touched.email && !!errors.email} message={errors.email} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => handleChange('company', e.target.value)}
                      onBlur={() => handleBlur('company')}
                      className={fieldClass('company')}
                      placeholder="Your company (optional)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Message *
                    </label>
                    <textarea
                      rows={4}
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      onBlur={() => handleBlur('message')}
                      className={`${fieldClass('message')} resize-none`}
                      placeholder="Tell us about your project..."
                      aria-invalid={!!(touched.message && errors.message)}
                    />
                    <div className="flex justify-between items-center mt-1">
                      <FieldError show={touched.message && !!errors.message} message={errors.message} />
                      <span
                        className={`text-xs ml-auto ${
                          formData.message.trim().length >= 10
                            ? 'text-gray-400 dark:text-gray-500'
                            : 'text-gray-400 dark:text-gray-500'
                        }`}
                      >
                        {formData.message.trim().length} / 10 min
                      </span>
                    </div>
                  </div>

                  {submitError && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-600 dark:text-red-400 text-sm">
                      <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                      <span>{submitError}</span>
                    </div>
                  )}

                  <motion.button
                    whileHover={isValid && !isSubmitting ? { scale: 1.02 } : {}}
                    whileTap={isValid && !isSubmitting ? { scale: 0.98 } : {}}
                    type="submit"
                    disabled={isSubmitting || !isValid}
                    className="w-full px-6 py-4 bg-primary dark:bg-highlight text-white font-semibold rounded-xl hover:bg-secondary dark:hover:bg-highlight/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    <Send size={18} />
                  </motion.button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const FieldError = ({ show, message }: { show: boolean; message?: string }) => (
  <AnimatePresence>
    {show && message && (
      <motion.p
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        className="mt-1.5 flex items-center gap-1 text-xs text-red-500"
      >
        <AlertCircle size={12} />
        {message}
      </motion.p>
    )}
  </AnimatePresence>
);

export default Contact;
